import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type SelectMenuOption = {
  value: string;
  label: string;
  /** Optional list-only label (e.g. crown). Trigger stays on `label`. */
  menuLabel?: string;
  disabled?: boolean;
  /** Optgroup-style section title. Consecutive options with the same group share a header. */
  group?: string;
  /** Credit (or other) pill shown on the trigger and in the list. */
  badge?: string;
};

type Props = {
  value: string;
  options: SelectMenuOption[];
  onChange: (value: string) => void;
  compact?: boolean;
  disabled?: boolean;
  'aria-label'?: string;
  id?: string;
  /** Open list max-height (default min(280px, 50vh); grouped menus can be taller). */
  maxHeight?: string | number;
};

const badgeBase: React.CSSProperties = {
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '1px 7px',
  borderRadius: 999,
  background: 'rgba(168,85,247,0.22)',
  border: '1px solid rgba(192,132,252,0.45)',
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: '0.02em',
  color: '#e9d5ff',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
};

function CreditBadge({ text, compact }: { text: string; compact?: boolean }) {
  return (
    <span
      aria-hidden
      style={{
        ...badgeBase,
        padding: compact ? '0 6px' : '1px 7px',
        fontSize: compact ? '0.58rem' : '0.62rem',
      }}
    >
      {text}
    </span>
  );
}
const MENU_GAP = 4;
const VIEWPORT_PAD = 8;
const DEFAULT_MAX_PX = 280;

const triggerBase: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,0.12)',
  background: 'rgba(0,0,0,0.35)',
  color: '#fff',
  fontFamily: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
  outline: 'none',
};

const listBase: React.CSSProperties = {
  position: 'fixed',
  zIndex: 10050,
  margin: 0,
  padding: 4,
  listStyle: 'none',
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.14)',
  background: 'rgba(14,14,20,0.98)',
  boxShadow: '0 16px 40px rgba(0,0,0,0.55)',
  overflowY: 'auto',
  overflowX: 'hidden',
  overscrollBehavior: 'contain',
};

type MenuPos = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

function resolvePreferredMaxHeight(maxHeight: string | number | undefined): number {
  if (typeof maxHeight === 'number' && Number.isFinite(maxHeight)) return maxHeight;
  if (typeof maxHeight === 'string') {
    const pxOnly = maxHeight.match(/^(\d+(?:\.\d+)?)px$/);
    if (pxOnly) return Number(pxOnly[1]);
    const minExpr = maxHeight.match(/min\(\s*(\d+(?:\.\d+)?)px\s*,\s*(\d+(?:\.\d+)?)vh\s*\)/i);
    if (minExpr) {
      return Math.min(Number(minExpr[1]), (Number(minExpr[2]) / 100) * window.innerHeight);
    }
    const vhOnly = maxHeight.match(/^(\d+(?:\.\d+)?)vh$/i);
    if (vhOnly) return (Number(vhOnly[1]) / 100) * window.innerHeight;
  }
  return Math.min(DEFAULT_MAX_PX, window.innerHeight * 0.5);
}

function computeMenuPos(trigger: HTMLElement, preferredMax: number): MenuPos {
  const rect = trigger.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_PAD;
  const spaceAbove = rect.top - MENU_GAP - VIEWPORT_PAD;
  const openUp = spaceBelow < Math.min(preferredMax, 160) && spaceAbove > spaceBelow;
  const available = Math.max(120, openUp ? spaceAbove : spaceBelow);
  const maxHeight = Math.min(preferredMax, available);
  const top = openUp
    ? Math.max(VIEWPORT_PAD, rect.top - MENU_GAP - maxHeight)
    : rect.bottom + MENU_GAP;
  const width = Math.min(Math.max(rect.width, 240), window.innerWidth - VIEWPORT_PAD * 2);
  const left = Math.min(rect.left, window.innerWidth - width - VIEWPORT_PAD);
  return {
    top,
    left: Math.max(VIEWPORT_PAD, left),
    width,
    maxHeight,
  };
}

export default function SelectMenu({
  value,
  options,
  onChange,
  compact,
  disabled,
  'aria-label': ariaLabel,
  id: idProp,
  maxHeight,
}: Props) {
  const reactId = useId();
  const listboxId = idProp || `select-menu-${reactId}`;
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);

  const selected = options.find((o) => o.value === value) || options[0];
  const enabledIndexes = options
    .map((o, i) => (o.disabled ? -1 : i))
    .filter((i) => i >= 0);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const updateMenuPos = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    setMenuPos(computeMenuPos(trigger, resolvePreferredMaxHeight(maxHeight)));
  }, [maxHeight]);

  const openMenu = useCallback(() => {
    if (disabled || options.length === 0) return;
    const selectedIdx = Math.max(
      0,
      options.findIndex((o) => o.value === value && !o.disabled),
    );
    setActiveIndex(selectedIdx >= 0 ? selectedIdx : enabledIndexes[0] ?? 0);
    setOpen(true);
  }, [disabled, enabledIndexes, options, value]);

  const selectAt = useCallback(
    (index: number) => {
      const opt = options[index];
      if (!opt || opt.disabled) return;
      onChange(opt.value);
      close();
    },
    [close, onChange, options],
  );

  const moveActive = useCallback(
    (delta: number) => {
      if (!enabledIndexes.length) return;
      const pos = enabledIndexes.indexOf(activeIndex);
      const nextPos =
        pos < 0
          ? 0
          : (pos + delta + enabledIndexes.length) % enabledIndexes.length;
      setActiveIndex(enabledIndexes[nextPos]);
    },
    [activeIndex, enabledIndexes],
  );

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
  }, [open, updateMenuPos, options.length]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target) || listRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', updateMenuPos);
    window.addEventListener('scroll', updateMenuPos, true);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', updateMenuPos);
      window.removeEventListener('scroll', updateMenuPos, true);
    };
  }, [close, open, updateMenuPos]);

  useEffect(() => {
    if (!open) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) openMenu();
      else moveActive(e.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (open) selectAt(activeIndex);
      else openMenu();
      return;
    }
    if (e.key === 'Home' && open) {
      e.preventDefault();
      if (enabledIndexes.length) setActiveIndex(enabledIndexes[0]);
      return;
    }
    if (e.key === 'End' && open) {
      e.preventDefault();
      if (enabledIndexes.length) setActiveIndex(enabledIndexes[enabledIndexes.length - 1]);
    }
  };

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveActive(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveActive(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      if (enabledIndexes.length) setActiveIndex(enabledIndexes[0]);
    } else if (e.key === 'End') {
      e.preventDefault();
      if (enabledIndexes.length) setActiveIndex(enabledIndexes[enabledIndexes.length - 1]);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      selectAt(activeIndex);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const pad = compact ? '6px 8px' : '8px 10px';
  const fontSize = compact ? '0.72rem' : '0.78rem';

  const listEl =
    open && menuPos ? (
      <ul
        ref={listRef}
        id={listboxId}
        role="listbox"
        tabIndex={-1}
        aria-labelledby={`${listboxId}-trigger`}
        aria-activedescendant={`${listboxId}-opt-${activeIndex}`}
        className="select-menu-list"
        onKeyDown={onListKeyDown}
        style={{
          ...listBase,
          top: menuPos.top,
          left: menuPos.left,
          width: menuPos.width,
          maxHeight: menuPos.maxHeight,
        }}
      >
        {options.map((opt, index) => {
          const isSelected = opt.value === value;
          const isActive = index === activeIndex;
          const prevGroup = index > 0 ? options[index - 1].group : undefined;
          const showGroup = Boolean(opt.group) && opt.group !== prevGroup;
          return (
            <React.Fragment key={opt.value}>
              {showGroup && (
                <li
                  role="presentation"
                  aria-hidden
                  style={{
                    padding: compact ? '8px 10px 3px' : '10px 12px 4px',
                    fontSize: compact ? '0.58rem' : '0.62rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.38)',
                    pointerEvents: 'none',
                  }}
                >
                  {opt.group}
                </li>
              )}
              <li
                id={`${listboxId}-opt-${index}`}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                role="option"
                aria-selected={isSelected}
                aria-disabled={opt.disabled || undefined}
                onMouseEnter={() => {
                  if (!opt.disabled) setActiveIndex(index);
                }}
                onClick={() => selectAt(index)}
                style={{
                  padding: compact ? '7px 10px' : '8px 12px',
                  borderRadius: 7,
                  fontSize,
                  fontFamily: 'inherit',
                  cursor: opt.disabled ? 'not-allowed' : 'pointer',
                  opacity: opt.disabled ? 0.4 : 1,
                  color: isSelected ? '#e9d5ff' : 'rgba(255,255,255,0.88)',
                  background: isActive
                    ? 'rgba(99,102,241,0.22)'
                    : isSelected
                      ? 'rgba(99,102,241,0.12)'
                      : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  outline: 'none',
                }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {opt.menuLabel || opt.label}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {opt.badge ? <CreditBadge text={opt.badge} compact={compact} /> : null}
                  {isSelected && (
                    <span aria-hidden style={{ color: '#a5b4fc', fontSize: '0.7rem' }}>
                      ✓
                    </span>
                  )}
                </span>
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    ) : null;

  return (
    <div ref={rootRef} style={{ position: 'relative', width: '100%' }}>
      <button
        ref={triggerRef}
        type="button"
        id={`${listboxId}-trigger`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-label={
          selected?.badge
            ? `${ariaLabel || 'Modelo'}: ${selected.label}, ${selected.badge}`
            : ariaLabel
        }
        onClick={() => (open ? close() : openMenu())}
        onKeyDown={onTriggerKeyDown}
        style={{
          ...triggerBase,
          padding: pad,
          fontSize,
          opacity: disabled ? 0.5 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          boxShadow: open ? '0 0 0 1px rgba(99,102,241,0.45)' : undefined,
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {selected?.label ?? '—'}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {selected?.badge ? <CreditBadge text={selected.badge} compact={compact} /> : null}
          <span
            aria-hidden
            style={{
              color: 'rgba(255,255,255,0.45)',
              fontSize: '0.65rem',
              transform: open ? 'rotate(180deg)' : undefined,
              transition: 'transform 0.15s ease',
            }}
          >
            ▾
          </span>
        </span>
      </button>

      {listEl && typeof document !== 'undefined' ? createPortal(listEl, document.body) : null}
    </div>
  );
}
